import ApiService from "../api";

// Interface para informações de contato
export interface IContact {
  uuid: string;
  email: string;
  phone: string;
}

// Interface para endereço
export interface IAddress {
  uuid: string;
  zip_code: string;
  street: string;
  number: string;
  district: string;
  city: string;
  state: string;
  country: string;
  additional_info: string;
  type: string;
}

// Interface para documento de identificação
export interface IIdentificationDocument {
  uuid: string;
  document_type: string;
  document: string;
  emission_date: string;
  expiration_date: string;
  pep: boolean;
  issuing_state: string;
}

// Interface para informações profissionais
export interface IProfessionalInfo {
  occupation_nature: string;
  employment_status: string;
  net_salary: number;
  additional_income: number;
  hiring_date: string;
  employee_number: string;
  department: string;
}

// Interface para informações dos pais
export interface IParent {
  uuid: string;
  parent_type: string;
  name: string;
  document_number: string;
  company_document_number: string;
  occupation: string;
  phone: string;
  birth_date: string;
  net_salary: number;
  additional_income: number;
}

// Interface para informações financeiras de empresas
export interface IFinancialInfo {
  uuid: string;
  initial_share_capital: number;
  net_equity: number;
  number_of_employees: number;
  average_monthly_revenue: number;
  average_gross_profit: number;
  exportation_percentage: number;
  suppliers_cost: number;
  tax_cost: number;
  last_12_months_revenue: number;
  leased_or_financed_value: number;
  payroll_value: number;
  pmv: number;
  funding_potential: number;
  late_sales_percentage: number;
}

export type IPartner = {
  uuid: string;
  type: string;
  name: string;
  document_number: string;
  document_type: string;
  contact: IContact;
  address: IAddress;
  birth_date: string;
  nationality: string;
  marital_status: string;
  occupation: string;
  social_name: string;
  birth_country: string;
  mother_name: string;
  father_name: string;
  gender: string;
  health_declaration: string;
  identification_document: IIdentificationDocument;
  professional_info: IProfessionalInfo;
  parents: IParent[];
};

export type IPartnerCompany = {
  uuid: string;
  type: string;
  name: string;
  document_number: string;
  document_type: "company" | "person";
  contact: IContact;
  address: IAddress;
  foundation_date: string;
  cnae: string;
  fantasy_name: string;
  legal_nature:
    | "sa"
    | "ltda"
    | "mei"
    | "eireli"
    | "simple_eireli"
    | "slu"
    | "fidc"
    | "scp"
    | "coop"
    | "pa"
    | "ssp"
    | "notary"
    | string;
  municipal_registration: string;
  state_registration: string;
  website: string;
  business_sector: string;
  employer_code: string;
  b3_account: string;
  finantial_info: IFinancialInfo;
};

// Tipo união para representar tanto pessoa física quanto jurídica
export type IPartnerUnion = IPartner | IPartnerCompany;

export type IPartnerDocumentsBody = {
  type: string;
  description: string;
  file: string;
  partner_uuid: string;
  file_url?: string;
};

export const getPartnersAPI = (params?: {
  document_type?: string;
  label?: string;
  legal_nature?: string;
}) => {
  return ApiService.HttpGet<IResponse<IPartner[]>>({
    route: "register/partner/",
    token: true,
    params,
  });
};

export const getPartnersDetailsAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<IPartner & IPartnerCompany>>({
    route: `register/partner/${uuid}/`,
    token: true,
  });
};

export const getPartnersCompanyAPI = (params?: { document_type?: string }) => {
  return ApiService.HttpGet<IResponse<IPartnerCompany[]>>({
    route: "register/partner/",
    token: true,
    params,
  });
};

export const getPartnersListAPI = (params?: { document_type?: string }) => {
  return ApiService.HttpGet<IResponse<IPartner[]>>({
    route: "register/partner/",
    token: true,
    params,
  });
};

export const getPartnerCompanyAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<IPartnerCompany>>({
    route: `register/partner/${uuid}/`,
    token: true,
  });
};

export const getPartnerPersonAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<unknown>>({
    route: `register/person/${uuid}/`,
    token: true,
  });
};

export const getPersonDetailsAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<IPartner>>({
    route: `register/person/${uuid}/`,
    token: true,
  });
};

export const getCompanyDetailsAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<IPartnerCompany>>({
    route: `register/company/${uuid}/`,
    token: true,
  });
};

export const postPartnerCompanyAPI = (body) => {
  return ApiService.HttpPost<IResponse<IPartnerCompany>>({
    route: `register/company/`,
    token: true,
    body,
  });
};

export const postPartnerPersonAPI = (body) => {
  return ApiService.HttpPost<IResponse<IPartnerCompany>>({
    route: `register/person/`,
    token: true,
    body,
  });
};


export const patchPartnerAPI = (body, uuid: string) => {
  return ApiService.HttpPatch<IResponse<IPartnerCompany>>({
    route: `register/partner/${uuid}/`,
    token: true,
    body,
  });
};

export const patchPersonAPI = (body, uuid: string) => {
  return ApiService.HttpPatch<IResponse<IPartner>>({
    route: `register/person/${uuid}/`,
    token: true,
    body,
  });
};

export const patchCompanyAPI = (body, uuid: string) => {
  return ApiService.HttpPatch<IResponse<IPartnerCompany>>({
    route: `register/company/${uuid}/`,
    token: true,
    body,
  });
};

export const getPartnerBankAccountAPI = (uuid: string) => {
  return ApiService.HttpGet<IResponse<unknown[]>>({
    route: `register/bank-account/`,
    token: true,
    params: { partner_uuid: uuid },
  });
};

export const postPartnerBankAccountAPI = (body) => {
  return ApiService.HttpPost<IResponse<unknown>>({
    route: `register/bank-account/`,
    token: true,
    body,
  });
};

export const putPartnerBankAccountAPI = (body, uuid: string) => {
  return ApiService.HttpPatch<IResponse<unknown>>({
    route: `register/bank-account/${uuid}/`,
    token: true,
    body,
  });
};

